import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SetStateAction } from 'react';

import type {
  AddressSearchResultFragment,
  MarketplacePropertyListFragment,
  MarketplacePropertySearchFragment,
  SavedSearchFieldsFragment,
} from '__generated__/athena/graphql';

import type { GeoPoint, MapLocation, MarketInfo } from '../map/types';
import { buildSaveSearchQuery, toPropertySearchQuery } from '../savedSearches/mapping';
import { canApplySavedSearchInPlace, pickPathname } from '../savedSearches/routing';
import { useSavedSearches } from '../savedSearches/useSavedSearches';
import { useMarketplaceSearch } from './useMarketplaceSearch';
import {
  buildQueryParams,
  buildVerticesQueryParams,
  SAVED_SEARCH_ID_PARAM,
  updateQueryParams,
} from './useSearchQueries';
import type { PropertySearchQuery } from './useSearchQueries';
import type { useSearchState } from './useSearchState';
import { buildPayloadFromQuery } from './useSearchState';

type SearchState = ReturnType<typeof useSearchState>;

interface UseMarketplaceSearchControllerArgs {
  initialSearch: MarketplacePropertySearchFragment;
  initialList: MarketplacePropertyListFragment | null;
  searchState: SearchState;
  marketInfo: MarketInfo;
  initialVertices?: GeoPoint[] | null;
  initialZoneIds?: string[] | null;
  /** `ssid` from the URL: the saved search this page view was applied from. */
  initialSavedSearchId?: string | null;
}

/**
 * The marketplace search session for `/homes/[market]`: it owns the result set
 * + loading flag (via `useMarketplaceSearch`) and the location the next search
 * runs against (map polygon, selected zone, or bare market), and exposes the
 * semantic search verbs the page used to hand-roll.
 *
 * Every entry point previously repeated the same "sync URL → build payload →
 * run → reset page" dance with a copy of the location expression; centralizing
 * it here keeps those flows in one tested place and stops the page (already a
 * large component) from accreting more orchestration.
 */
export const useMarketplaceSearchController = ({
  initialSearch,
  initialList,
  searchState,
  marketInfo,
  initialVertices,
  initialZoneIds,
  initialSavedSearchId,
}: UseMarketplaceSearchControllerArgs) => {
  const {
    propertySearchData,
    propertyListData,
    setPropertyListData,
    isLoading,
    setIsLoading,
    hasError,
    clearError,
    runSearch,
    loadNextPage,
  } = useMarketplaceSearch(initialSearch, initialList);

  // The custom polygon driving results, or null when none is active (searches
  // then fall back to the market boundary). Holding "no polygon" as null —
  // rather than seeding the state with the market shape — keeps "is this a
  // drawn area?" answerable from the state itself, which save-search relies on
  // to persist a clean market-level record instead of a polygon.
  const [drawnVertices, setDrawnVertices] = useState<GeoPoint[] | null>(
    initialVertices?.length ? initialVertices : null,
  );
  const [selectedLocation, setSelectedLocationState] = useState<MapLocation | undefined>();
  // Vertices win over zones (same precedence as SSR / Athena). When the page
  // hydrates with a viewport polygon, ignore route zone ids so client searches
  // and saves keep the polygon instead of silently expanding to the whole zone.
  const [routeZoneIds, setRouteZoneIds] = useState<string[] | null>(() => {
    if (initialVertices?.length) {
      return null;
    }
    return initialZoneIds?.length ? initialZoneIds : null;
  });
  const [selectedAddress, setSelectedAddress] = useState<AddressSearchResultFragment | undefined>();

  // The saved search this page's results came from (URL `ssid` on load, or the
  // last applied/created one). Held as an id and resolved against the shared
  // saved-searches store so renames and updates from any surface flow through,
  // and a deletion clears it.
  const { savedSearches, hasLoaded: savedSearchesLoaded } = useSavedSearches();
  const [activeSavedSearchId, setActiveSavedSearchId] = useState<string | null>(
    initialSavedSearchId ?? null,
  );
  const activeSavedSearch = useMemo(
    () => savedSearches.find((saved) => saved.id === activeSavedSearchId) ?? null,
    [savedSearches, activeSavedSearchId],
  );

  // Marks a saved search as the page's active one (e.g. right after "save as
  // new") and stamps its id into the URL so reload/share restores it.
  const markActiveSavedSearch = useCallback((savedSearchId: string) => {
    if (!savedSearchId) {
      return;
    }
    setActiveSavedSearchId(savedSearchId);
    const params = new URLSearchParams(window.location.search);
    params.set(SAVED_SEARCH_ID_PARAM, savedSearchId);
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
  }, []);

  // Clears the active saved-search association. Used when the user explicitly
  // resets filters so a subsequent save starts fresh instead of offering to
  // update the search they departed from, and when the loaded list no longer
  // contains the active id (deleted / invalid ssid).
  const clearActiveSavedSearch = useCallback(() => {
    setActiveSavedSearchId(null);
    const params = new URLSearchParams(window.location.search);
    if (!params.has(SAVED_SEARCH_ID_PARAM)) {
      return;
    }
    params.delete(SAVED_SEARCH_ID_PARAM);
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
  }, []);

  useEffect(() => {
    // Only reconcile against a genuinely loaded list; transient empties during
    // auth churn must not wipe the active reference.
    if (
      savedSearchesLoaded &&
      activeSavedSearchId &&
      !savedSearches.some((saved) => saved.id === activeSavedSearchId)
    ) {
      clearActiveSavedSearch();
    }
  }, [savedSearchesLoaded, savedSearches, activeSavedSearchId, clearActiveSavedSearch]);

  const setSelectedLocation = useCallback((location: SetStateAction<MapLocation | undefined>) => {
    setSelectedLocationState((previous) => {
      const next = typeof location === 'function' ? location(previous) : location;
      if (!next) {
        setRouteZoneIds(null);
      }
      return next;
    });
  }, []);

  // The location the next search runs against. `buildPayloadFromQuery` treats
  // these as mutually exclusive (vertices > zoneIds > market); zone selection
  // clears `vertices` so a chosen zone wins, otherwise the current map polygon
  // (or, when empty, the bare market) drives results.
  const activeZoneIds = useMemo(() => {
    if (selectedLocation?.value) {
      return [selectedLocation.value];
    }
    return routeZoneIds?.length ? routeZoneIds : null;
  }, [routeZoneIds, selectedLocation?.value]);

  const currentSearchLocation: Pick<PropertySearchQuery, 'market' | 'zoneIds' | 'vertices'> =
    useMemo(
      () => ({
        market: marketInfo.name,
        // With no zone and no drawn polygon, searches still run against the
        // market's boundary polygon (not the bare market name) so results match
        // what the map shows.
        vertices: activeZoneIds?.length ? null : (drawnVertices ?? marketInfo.shape.coordinates[0]),
        zoneIds: activeZoneIds,
      }),
      [marketInfo.name, marketInfo.shape, drawnVertices, activeZoneIds],
    );

  // Runs a search against the current location, optionally overriding filter
  // values being set in the same tick (the async filter setters wouldn't be
  // visible to `buildSearchPayload` yet). Backs both the quick and full filter
  // menus.
  const applyFilters = useCallback(
    async (filterOverride: Partial<PropertySearchQuery> = {}) => {
      const status = await runSearch(
        searchState.buildSearchPayload({ ...filterOverride, ...currentSearchLocation }),
      );
      // Only rewrite the URL / reset the page once the filtered results are on
      // screen. A failed (e.g. Athena rate-limit) or superseded fetch must not
      // leave the URL advertising filters the visible listings don't reflect;
      // the previous applied query stays in the URL so a retry is consistent.
      if (status !== 'applied') {
        return;
      }
      updateQueryParams({ ...searchState.getCanonical(), ...filterOverride });
      searchState.resetPage();
    },
    [searchState, runSearch, currentSearchLocation],
  );

  // Runs a zone search for a city/neighborhood picked from location search.
  //
  // Unlike `applyViewport` (which commits its location only once results apply),
  // this commits `selectedLocation` up front: the pick came from an explicit
  // dropdown selection, so the search box/label should reflect it immediately,
  // and keeping it on failure preserves the selection for a retry.
  // `setDrawnVertices(null)` is not what makes the zone drive results — the
  // `currentSearchLocation` memo already nulls `vertices` whenever a zone is
  // selected — it drops the now-defunct map polygon so a later save-search or
  // post-deselect market search can't resurrect it (which would otherwise
  // override the zone via the `vertices > zoneIds` precedence).
  //
  // The URL is only rewritten to the canonical `/homes/zid_<id>` route once
  // results are on screen, so a failed/superseded fetch leaves the URL matching
  // the listings still shown.
  const applyZoneSearch = useCallback(
    async (location: MapLocation) => {
      const zoneId = location.value;
      if (!zoneId) {
        return;
      }

      setSelectedLocation(location);
      setRouteZoneIds([zoneId]);
      setDrawnVertices(null);

      const status = await runSearch(
        searchState.buildSearchPayload({ zoneIds: [zoneId], vertices: null }),
      );
      if (status !== 'applied') {
        return;
      }

      const params = buildQueryParams(searchState.getCanonical(), { seedFromUrl: false });
      window.history.replaceState({}, '', `/homes/zid_${zoneId}?${params.toString()}`);
      searchState.resetPage();
    },
    [searchState, runSearch],
  );

  // Runs a polygon search for a freshly drawn/panned map viewport and, once its
  // results are on screen, adopts it as the active location, clearing any
  // selected zone so later "load more" / "save search" actions don't reuse the
  // stale zone.
  const applyViewport = useCallback(
    async (coordinates: GeoPoint[]) => {
      const status = await runSearch(searchState.buildSearchPayload({ vertices: coordinates }));
      // A failed or superseded pan (e.g. a rapid pan replaced by a newer one, or
      // a saved-search selection mid-flight) must not strand the URL, list, or
      // active location on a viewport whose results never applied — that would
      // leave later load-more / save-search actions using a polygon the visible
      // results and URL don't match.
      if (status !== 'applied') {
        return;
      }

      const queryString = buildVerticesQueryParams(coordinates);
      const pathname = marketInfo.identifier
        ? `/homes/${marketInfo.identifier}`
        : window.location.pathname;
      window.history.replaceState({}, '', `${pathname}?${queryString}`);
      updateQueryParams(searchState.getCanonical());

      searchState.resetPage();
      setDrawnVertices(coordinates);
      setSelectedLocation(undefined);
      setRouteZoneIds(null);
    },
    [searchState, runSearch, marketInfo.identifier],
  );

  // Appends the next page of list results for the current search.
  const loadMore = useCallback(async () => {
    if (!propertyListData?.nextPage) {
      return;
    }
    const nextPage = searchState.get.pageNumber + 1;
    const status = await loadNextPage(
      searchState.buildSearchPayload(currentSearchLocation),
      nextPage,
    );
    // Only advance the page counter when a page was actually appended; on a
    // failed fetch the hook flags the error and the "see more" affordance stays
    // so the user can retry.
    if (status === 'appended') {
      searchState.set.setPageNumber(nextPage);
    }
  }, [propertyListData?.nextPage, searchState, currentSearchLocation, loadNextPage]);

  // Applies a saved search selected from the dropdown. A same-market,
  // market-level search is applied in place (refetch + update filters + rewrite
  // the URL) so the user stays in their session. Zone/polygon searches and
  // cross-market switches need fresh SSR data (market shape, coordinates, zone
  // resolution), so we hard-navigate for those — the long-lived page never
  // re-hydrates its mount-time state from new props, so a soft route change
  // would leave results stale.
  const applySavedSearch = useCallback(
    async (savedSearch: SavedSearchFieldsFragment) => {
      const query = toPropertySearchQuery(savedSearch);
      const definedQuery = Object.fromEntries(
        Object.entries(query).filter(([, value]) => value !== undefined),
      );

      if (!canApplySavedSearchInPlace(savedSearch, marketInfo.identifier)) {
        const params = buildQueryParams(definedQuery, { seedFromUrl: false });
        params.set(SAVED_SEARCH_ID_PARAM, savedSearch.id);
        window.location.assign(`${pickPathname(savedSearch)}?${params.toString()}`);
        return;
      }

      // Refetch from the saved query directly first (the state setters below
      // won't be visible to `buildSearchPayload` this tick). Only commit the
      // in-place UI/URL changes once we actually have results — a failed or
      // superseded fetch must not leave the page showing stale listings under
      // the saved-search filters and URL.
      const status = await runSearch(buildPayloadFromQuery(query, { market: marketInfo.name }));
      if (status !== 'applied') {
        return;
      }

      searchState.applyCanonical(query);
      setSelectedLocation(undefined);
      setSelectedAddress(undefined);
      // Only market-level searches apply in place, so the applied state has no
      // drawn polygon.
      setDrawnVertices(null);
      setRouteZoneIds(null);
      searchState.resetPage();
      setActiveSavedSearchId(savedSearch.id);

      // Rewrite the URL to the canonical market path explicitly. Reusing the
      // current pathname would strand a stale zone route (e.g. `/homes/zid_x`)
      // that re-SSRs the old zone search on reload/share/back.
      const params = buildQueryParams(definedQuery, { seedFromUrl: false });
      params.set(SAVED_SEARCH_ID_PARAM, savedSearch.id);
      window.history.replaceState({}, '', `/homes/${marketInfo.identifier}?${params.toString()}`);
    },
    [marketInfo, searchState, runSearch],
  );

  // The canonical query handed to the save-search button. `getCanonical()`
  // omits location, so the active market/zone/polygon is merged back in here.
  // We persist the routing slug (`identifier`), never the display `name`, so a
  // zone page's label isn't written into the saved search and later routed to a
  // non-existent `/homes/<label>`. Only a genuinely drawn polygon is persisted
  // as vertices — with none, the search saves as a clean market-level record.
  const getSaveSearchQuery = useCallback(
    (): PropertySearchQuery =>
      buildSaveSearchQuery({
        canonical: searchState.getCanonical(),
        marketIdentifier: marketInfo.identifier,
        selectedZoneId: selectedLocation?.value,
        routeZoneIds,
        vertices: activeZoneIds?.length ? null : drawnVertices,
      }),
    [
      searchState,
      marketInfo.identifier,
      selectedLocation?.value,
      routeZoneIds,
      activeZoneIds,
      drawnVertices,
    ],
  );

  return {
    propertySearchData,
    propertyListData,
    setPropertyListData,
    isLoading,
    setIsLoading,
    hasError,
    clearError,
    selectedLocation,
    setSelectedLocation,
    selectedAddress,
    setSelectedAddress,
    applyFilters,
    applyZoneSearch,
    applyViewport,
    loadMore,
    applySavedSearch,
    getSaveSearchQuery,
    activeSavedSearch,
    markActiveSavedSearch,
    clearActiveSavedSearch,
  };
};
